import cx from 'classnames'
import { useCallback, useContext, useEffect, useMemo, useRef } from 'react'
import { useMediaQuery, useResizeObserver } from 'usehooks-ts'

import { FrameBuilderSettingsContext } from '@frame-builder/contexts/settings-context'
import {
  FrameBuilderDefaultStateContext,
  FrameBuilderSelectedStateContext,
} from '@frame-builder/contexts/state-context'
import { defaultCamera } from '@frame-builder/lib/frame-viewer/camera'
import { getFrameSettings } from '@frame-builder/lib/frame-viewer/settings'

import FrameBuilderActions from './actions'
import FrameViewer from './frame-viewer'
import FrameBuilderGallery from './gallery'

interface FrameBuilderProps {
  showProgressBar: boolean
  openDimensionDetails: boolean
}

const FrameBuilder = ({
  showProgressBar,
  openDimensionDetails,
}: FrameBuilderProps) => {
  const { defaultState } = useContext(FrameBuilderDefaultStateContext)
  const { isSelectedStateLoaded, selectedState } = useContext(
    FrameBuilderSelectedStateContext,
  )
  const { frameBuilderConfiguration, frameBuilderType } = useContext(
    FrameBuilderSettingsContext,
  )

  const frameViewerRef = useRef<HTMLCanvasElement | null>(null)

  const isScreenSmallOrLarger = useMediaQuery(`(min-width: 768px)`)

  // Calculated widths and heights
  const layoutRef = useRef<HTMLDivElement | null>(null)
  const builderRef = useRef<HTMLDivElement | null>(null)
  const frameViewerWrapperRef = useRef<HTMLDivElement | null>(null)
  const layoutRectangle = useResizeObserver({
    ref: layoutRef,
  })
  const builderRectangle = useResizeObserver({
    ref: builderRef,
  })
  const frameViewerWrapperRectangle = useResizeObserver({
    ref: frameViewerWrapperRef,
  })

  const frameViewerContainerSize = useMemo(() => {
    if (
      !isScreenSmallOrLarger ||
      !layoutRectangle.width ||
      !layoutRectangle.height ||
      !builderRectangle.width ||
      !builderRectangle.height
    ) {
      return [null, null] as const
    }

    return [
      layoutRectangle.width - builderRectangle.width - 20,
      isScreenSmallOrLarger ? layoutRectangle.width * 0.8 : null,
    ] as const
  }, [isScreenSmallOrLarger, layoutRectangle, builderRectangle])
  const viewerSize = useMemo(() => {
    if (
      !frameViewerWrapperRectangle.width ||
      !frameViewerWrapperRectangle.height
    ) {
      return [null, null] as const
    }

    return [
      frameViewerWrapperRectangle.width,
      isScreenSmallOrLarger
        ? frameViewerWrapperRectangle.height - 80
        : frameViewerWrapperRectangle.width,
    ] as const
  }, [isScreenSmallOrLarger, frameViewerWrapperRectangle])

  const galleryImages = useMemo(() => {
    const profileTexture = frameBuilderConfiguration.profileTextures.find(
      (profileTexture) =>
        selectedState.profile &&
        selectedState.texture &&
        profileTexture.profile._ref === selectedState.profile._id &&
        profileTexture.texture._ref === selectedState.texture._id,
    )

    return profileTexture?.gallery ?? []
  }, [frameBuilderConfiguration.profileTextures, selectedState])

  const frameSettings = useMemo(() => {
    return getFrameSettings(
      frameBuilderType,
      frameBuilderConfiguration,
      selectedState,
      defaultState,
    )
  }, [defaultState, frameBuilderConfiguration, frameBuilderType, selectedState])

  const backgroundColor = useMemo(() => {
    return (
      selectedState.artwork.backgroundColor?.current ??
      defaultState?.artwork?.backgroundColor?.current
    )
  }, [defaultState, selectedState])

  const getProductImageDataUrl = useCallback(() => {
    if (!frameViewerRef.current) {
      return
    }

    // Use JPEG format with limited quality to prevent request size errors
    const imageDataUrl = frameViewerRef.current.toDataURL('image/jpeg', 0.75)

    return imageDataUrl
  }, [frameViewerRef])

  useEffect(() => {
    // Trigger window resize to read correct sizes of all elements
    setTimeout(() => {
      window.dispatchEvent(new Event('resize'))
    }, 0)
  }, [])

  return (
    <section
      className={cx('container transition-opacity duration-200', {
        'opacity-0': !isSelectedStateLoaded,
      })}
    >
      <div
        ref={layoutRef}
        className="flex flex-col-reverse justify-between sm:flex-row gap-8"
      >
        <FrameBuilderActions
          ref={builderRef}
          showProgressBar={showProgressBar}
          layoutRectangle={layoutRectangle}
          viewerSize={viewerSize}
          openDimensionDetails={openDimensionDetails}
          getProductImageDataUrl={getProductImageDataUrl}
          className="shrink-0"
        />
        <div
          className="flex sm:block flex-col items-center relative w-full h-full"
          style={{
            width: frameViewerContainerSize[0] ?? undefined,
            height: frameViewerContainerSize[1] ?? undefined,
            backgroundColor,
          }}
        >
          <div ref={frameViewerWrapperRef} className="w-full sm:h-full">
            <FrameViewer
              ref={frameViewerRef}
              frameSettings={frameSettings}
              camera={defaultCamera}
              width={frameViewerWrapperRectangle.width}
              height={frameViewerWrapperRectangle.width}
            />
          </div>

          {galleryImages.length > 0 && (
            <FrameBuilderGallery
              images={galleryImages}
              className="sm:absolute sm:right-0 md:right-2.5 lg:right-8 sm:top-1/2 sm:-translate-y-1/2 z-20"
            />
          )}
        </div>
      </div>
    </section>
  )
}

export default FrameBuilder
